// import React, { useState, FormEvent } from 'react';
// import { CreateUserRequest } from '../types/user';
// import '../styles/UserForm.css';

// interface Props {
//     onSubmit: (data: CreateUserRequest) => Promise<void>;
//     onCancel: () => void;
//     allowedRoles: ('admin' | 'viewer')[];
// }

// export default function UserForm({ onSubmit, onCancel, allowedRoles }: Props) {
//     const [form, setForm] = useState<CreateUserRequest>({
//         email: '',
//         username: '',
//         password: '',
//         role: allowedRoles[0],
//     });
//     const [error, setError] = useState<string>('');

//     const handleChange = (
//         e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
//     ) => {
//         setForm({ ...form, [e.target.name]: e.target.value } as any);
//     };

//     const handleSubmit = async (e: FormEvent) => {
//         e.preventDefault();
//         setError('');
//         try {
//             await onSubmit(form);
//         } catch (err: any) {
//             setError(err.response?.data?.message || err.message);
//         }
//     };

//     return (
//         <div className="user-form-overlay">
//             <div className="user-form-container">
//                 <div className="user-form-header">
//                     <h1 className="user-form-title">Create New User</h1>
//                 </div>
//                 {error && <div className="user-form-error">{error}</div>}

//                 <form className="user-form-grid" onSubmit={handleSubmit}>
//                     <div className="user-form-group" style={{ gridColumn: '1/-1' }}>
//                         <label className="user-form-label" htmlFor="email">
//                             Email
//                         </label>
//                         <input
//                             id="email"
//                             name="email"
//                             type="email"
//                             className="user-form-input"
//                             value={form.email}
//                             onChange={handleChange}
//                             required
//                             placeholder="user@example.com"
//                             autoComplete="off"
//                         />
//                     </div>
//                     <div className="user-form-group">
//                         <label className="user-form-label" htmlFor="username">
//                             Username
//                         </label>
//                         <input
//                             id="username"
//                             name="username"
//                             type="text"
//                             className="user-form-input"
//                             value={form.username}
//                             onChange={handleChange}
//                             required
//                             minLength={3}
//                             placeholder="Username"
//                             autoComplete="off"
//                         />
//                     </div>
//                     <div className="user-form-group">
//                         <label className="user-form-label" htmlFor="password">
//                             Password
//                         </label>
//                         <input
//                             id="password"
//                             name="password"
//                             type="password"
//                             className="user-form-input"
//                             value={form.password}
//                             onChange={handleChange}
//                             required
//                             minLength={8}
//                             placeholder="Password"
//                             autoComplete="new-password"
//                         />
//                     </div>
//                     <div className="user-form-group">
//                         <label className="user-form-label" htmlFor="role">
//                             Role
//                         </label>
//                         <select
//                             id="role"
//                             name="role"
//                             className="user-form-input"
//                             value={form.role}
//                             onChange={handleChange}
//                             required
//                         >
//                             {allowedRoles.map((r) => (
//                                 <option key={r} value={r}>
//                                     {r.charAt(0).toUpperCase() + r.slice(1)}
//                                 </option>
//                             ))}
//                         </select>
//                     </div>
//                     <div className="user-button-group" style={{ gridColumn: '1/-1' }}>
//                         <button
//                             type="button"
//                             className="user-btn user-btn-cancel"
//                             onClick={onCancel}
//                         >
//                             Cancel
//                         </button>
//                         <button
//                             type="submit"
//                             className="user-btn user-btn-save"
//                         >
//                             Create
//                         </button>
//                     </div>
//                 </form>
//             </div>
//         </div>
//     );
// }

import React, { FormEvent, useEffect, useId, useRef, useState } from "react";

import {
  AtSign,
  Eye,
  EyeOff,
  KeyRound,
  Mail,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";

import { CreateUserRequest } from "../types/user";

interface Props {
  onSubmit: (data: CreateUserRequest) => Promise<void>;

  onCancel: () => void;

  allowedRoles: ("admin" | "viewer")[];
}

/* =========================================================
   COMPONENT
========================================================= */

export default function UserForm({ onSubmit, onCancel, allowedRoles }: Props) {
  const [form, setForm] = useState<CreateUserRequest>({
    email: "",
    username: "",
    password: "",
    role: allowedRoles[0],
  });

  const [error, setError] = useState("");

  const [submitting, setSubmitting] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  const emailRef = useRef<HTMLInputElement>(null);

  const titleId = useId();

  const descriptionId = useId();

  /* =====================================================
       DIALOG BEHAVIOUR
    ===================================================== */

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    window.setTimeout(() => {
      emailRef.current?.focus();
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !submitting) {
        onCancel();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;

      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onCancel, submitting]);

  /* =====================================================
       CHANGE
    ===================================================== */

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    setForm((current) => ({
      ...current,

      [event.target.name]: event.target.value,
    }));

    if (error) {
      setError("");
    }
  };

  /* =====================================================
       SUBMIT
    ===================================================== */

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();

    if (submitting) {
      return;
    }

    setError("");
    setSubmitting(true);

    try {
      await onSubmit(form);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Unable to create user.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  /* =====================================================
       SHARED STYLES
    ===================================================== */

  const inputClass = `
        box-border
        h-10
        w-full
        rounded-[9px]
        border
        border-slate-200
        bg-white
        px-3
        text-[12px]
        text-slate-900
        outline-none
        transition
        placeholder:text-slate-400
        hover:border-slate-300
        focus:border-slate-400
        focus:ring-2
        focus:ring-slate-100
        disabled:cursor-not-allowed
        disabled:bg-slate-50
        disabled:text-slate-500
    `;

  /* =====================================================
       RENDER
    ===================================================== */

  return (
    <div
      role="presentation"
      className="
                fixed
                inset-0
                z-[1200]
                flex
                items-center
                justify-center
                bg-slate-950/45
                p-4
                backdrop-blur-[1px]
            "
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !submitting) {
          onCancel();
        }
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}
        className="
                    flex
                    max-h-[calc(100vh-32px)]
                    w-full
                    max-w-[560px]
                    flex-col
                    overflow-hidden
                    rounded-[18px]
                    border
                    border-slate-200
                    bg-white
                    shadow-[0_24px_80px_rgba(15,23,42,0.25)]
                "
      >
        {/* =========================================
                    HEADER
                ========================================= */}

        <header
          className="
                        flex
                        shrink-0
                        items-start
                        justify-between
                        gap-4
                        border-b
                        border-slate-100
                        px-6
                        py-5

                        max-sm:px-4
                    "
        >
          <div
            className="
                            flex
                            min-w-0
                            items-start
                            gap-3
                        "
          >
            <div
              className="
                                grid
                                h-10
                                w-10
                                shrink-0
                                place-items-center
                                rounded-[11px]
                                bg-slate-100
                                text-slate-600
                            "
            >
              <UserRound size={19} strokeWidth={1.8} />
            </div>

            <div>
              <h2
                id={titleId}
                className="
                                    m-0
                                    text-[18px]
                                    font-bold
                                    tracking-[-0.02em]
                                    text-slate-950
                                "
              >
                Create New User
              </h2>

              <p
                id={descriptionId}
                className="
                                    mb-0
                                    mt-1
                                    text-[10px]
                                    leading-4
                                    text-slate-400
                                "
              >
                Create a system account and assign its initial access role.
              </p>
            </div>
          </div>

          <button
            type="button"
            aria-label="Close"
            title="Close"
            disabled={submitting}
            onClick={onCancel}
            className="
                            grid
                            h-8
                            w-8
                            shrink-0
                            place-items-center
                            rounded-lg
                            border-0
                            bg-transparent
                            p-0
                            text-slate-400
                            transition
                            hover:bg-slate-100
                            hover:text-slate-800
                            focus:outline-none
                            focus:ring-2
                            focus:ring-slate-200
                            disabled:cursor-not-allowed
                            disabled:opacity-40
                        "
          >
            <X size={17} />
          </button>
        </header>

        {/* =========================================
                    FORM
                ========================================= */}

        <form
          onSubmit={handleSubmit}
          className="
                        flex
                        min-h-0
                        flex-1
                        flex-col
                        overflow-hidden
                    "
        >
          {/* =====================================
                        BODY
                    ===================================== */}

          <div
            className="
                            min-h-0
                            flex-1
                            overflow-y-auto
                            px-6
                            py-5

                            max-sm:px-4
                        "
          >
            {/* =================================
                            ERROR
                        ================================= */}

            {error && (
              <div
                role="alert"
                className="
                                    mb-4
                                    rounded-[9px]
                                    border
                                    border-red-200
                                    bg-red-50
                                    px-3
                                    py-2.5
                                    text-[11px]
                                    font-medium
                                    leading-4
                                    text-red-700
                                "
              >
                {error}
              </div>
            )}

            <div
              className="
                                grid
                                grid-cols-2
                                gap-x-3
                                gap-y-4

                                max-sm:grid-cols-1
                            "
            >
              {/* =============================
                                EMAIL
                            ============================= */}

              <label
                className="
                                    col-span-2
                                    block

                                    max-sm:col-span-1
                                "
              >
                <span
                  className="
                                        mb-1.5
                                        block
                                        text-[11px]
                                        font-semibold
                                        text-slate-700
                                    "
                >
                  Email
                  <span className="ml-1 text-red-500">*</span>
                </span>

                <div className="relative">
                  <Mail
                    size={15}
                    strokeWidth={1.8}
                    className="
                                            pointer-events-none
                                            absolute
                                            left-3
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  />

                  <input
                    ref={emailRef}
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    placeholder="user@example.com"
                    autoComplete="off"
                    className={`
                                            ${inputClass}
                                            pl-9
                                        `}
                  />
                </div>
              </label>

              {/* =============================
                                USERNAME
                            ============================= */}

              <label className="block">
                <span
                  className="
                                        mb-1.5
                                        block
                                        text-[11px]
                                        font-semibold
                                        text-slate-700
                                    "
                >
                  Username
                  <span className="ml-1 text-red-500">*</span>
                </span>

                <div className="relative">
                  <AtSign
                    size={15}
                    strokeWidth={1.8}
                    className="
                                            pointer-events-none
                                            absolute
                                            left-3
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  />

                  <input
                    id="username"
                    name="username"
                    type="text"
                    value={form.username}
                    onChange={handleChange}
                    required
                    minLength={3}
                    disabled={submitting}
                    placeholder="Username"
                    autoComplete="off"
                    className={`
                                            ${inputClass}
                                            pl-9
                                        `}
                  />
                </div>

                <span
                  className="
                                        mt-1
                                        block
                                        text-[9px]
                                        text-slate-400
                                    "
                >
                  Minimum 3 characters.
                </span>
              </label>

              {/* =============================
                                PASSWORD
                            ============================= */}

              <label className="block">
                <span
                  className="
                                        mb-1.5
                                        block
                                        text-[11px]
                                        font-semibold
                                        text-slate-700
                                    "
                >
                  Password
                  <span className="ml-1 text-red-500">*</span>
                </span>

                <div className="relative">
                  <KeyRound
                    size={15}
                    strokeWidth={1.8}
                    className="
                                            pointer-events-none
                                            absolute
                                            left-3
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  />

                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={form.password}
                    onChange={handleChange}
                    required
                    minLength={8}
                    disabled={submitting}
                    placeholder="Password"
                    autoComplete="new-password"
                    className={`
                                            ${inputClass}
                                            pl-9 pr-10
                                        `}
                  />

                  <button
                    type="button"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    title={showPassword ? "Hide password" : "Show password"}
                    disabled={submitting}
                    onClick={() => setShowPassword((current) => !current)}
                    className="
                                            absolute
                                            right-2
                                            top-1/2
                                            grid
                                            h-7
                                            w-7
                                            -translate-y-1/2
                                            place-items-center
                                            rounded-md
                                            border-0
                                            bg-transparent
                                            p-0
                                            text-slate-400
                                            transition
                                            hover:bg-slate-100
                                            hover:text-slate-700
                                        "
                  >
                    {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>

                <span
                  className="
                                        mt-1
                                        block
                                        text-[9px]
                                        text-slate-400
                                    "
                >
                  Minimum 8 characters.
                </span>
              </label>

              {/* =============================
                                ROLE
                            ============================= */}

              <label
                className="
                                    col-span-2
                                    block

                                    max-sm:col-span-1
                                "
              >
                <span
                  className="
                                        mb-1.5
                                        block
                                        text-[11px]
                                        font-semibold
                                        text-slate-700
                                    "
                >
                  Role
                  <span className="ml-1 text-red-500">*</span>
                </span>

                <div className="relative">
                  <ShieldCheck
                    size={15}
                    strokeWidth={1.8}
                    className="
                                            pointer-events-none
                                            absolute
                                            left-3
                                            top-1/2
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  />

                  <select
                    id="role"
                    name="role"
                    value={form.role}
                    onChange={handleChange}
                    required
                    disabled={submitting}
                    className={`
                                            ${inputClass}
                                            appearance-none
                                            pl-9 pr-9
                                        `}
                  >
                    {allowedRoles.map((role) => (
                      <option key={role} value={role}>
                        {role.charAt(0).toUpperCase() + role.slice(1)}
                      </option>
                    ))}
                  </select>

                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                    className="
                                            pointer-events-none
                                            absolute
                                            right-3
                                            top-1/2
                                            h-4
                                            w-4
                                            -translate-y-1/2
                                            text-slate-400
                                        "
                  >
                    <path
                      d="m7 10 5 5 5-5"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                <span
                  className="
                                        mt-1
                                        block
                                        text-[9px]
                                        text-slate-400
                                    "
                >
                  Determines the user's system permissions.
                </span>
              </label>
            </div>
          </div>

          {/* =====================================
                        FOOTER
                    ===================================== */}

          <footer
            className="
                            flex
                            shrink-0
                            items-center
                            justify-end
                            gap-2
                            border-t
                            border-slate-100
                            bg-white
                            px-6
                            py-4

                            max-sm:px-4
                        "
          >
            <button
              type="button"
              disabled={submitting}
              onClick={onCancel}
              className="
                                h-10
                                min-w-[100px]
                                rounded-[9px]
                                border
                                border-slate-200
                                bg-white
                                px-4
                                text-[11px]
                                font-semibold
                                text-slate-700
                                transition
                                hover:border-slate-300
                                hover:bg-slate-50
                                disabled:cursor-not-allowed
                                disabled:opacity-50

                                max-sm:flex-1
                            "
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="
                                inline-flex
                                h-10
                                min-w-[120px]
                                items-center
                                justify-center
                                gap-2
                                rounded-[9px]
                                border-0
                                bg-[#0d162a]
                                px-4
                                text-[11px]
                                font-semibold
                                text-white
                                transition
                                hover:bg-[#172239]
                                focus:outline-none
                                focus:ring-2
                                focus:ring-slate-300
                                disabled:cursor-not-allowed
                                disabled:opacity-50

                                max-sm:flex-1
                            "
            >
              {submitting ? (
                <>
                  <span
                    className="
                                            h-3.5
                                            w-3.5
                                            animate-spin
                                            rounded-full
                                            border-2
                                            border-white/30
                                            border-t-white
                                        "
                  />
                  Creating...
                </>
              ) : (
                "Create User"
              )}
            </button>
          </footer>
        </form>
      </section>
    </div>
  );
}
